import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { TurfTalk } from "@/api/client";
import { Chat } from "@/components/Chat";
import { GameNews } from "@/components/GameNews";
import { buildDiscussMessage } from "@/lib/news";
import { createStorySharer, SHARE_RESULT } from "@/lib/roomShare";

// Room chat with an optional side Game News panel (standalone room route). On
// the embedded game page the side panel is suppressed (the page shows the news
// feed above the tabs) and the game page owns the "Debate this" hand-off; here
// `refreshNonce` lets the page nudge this chat after a confirmed share.
//
// The in-panel "Discuss" path (standalone route) marks a story shared ONLY on a
// confirmed POST, reuses the per-story idempotency key on retry, and guards
// against room switches / unmount so a stale completion can't mutate the new
// room's state. Each roomId gets its own isolated controller + state.
export function RoomWithNews({ roomId, title, gameId = null, league = null, team = null, height = 560, embedded = false, refreshNonce = 0 }) {
  const [internalRefresh, setInternalRefresh] = useState(0);
  const [discussing, setDiscussing] = useState(null); // id currently posting
  const [shared, setShared] = useState(() => new Set()); // confirmed ids (this room)
  const mountedRef = useRef(false);
  const roomIdRef = useRef(roomId);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  // Isolate per room: a room switch resets in-panel state and invalidates any
  // in-flight completion that resolves afterwards.
  useEffect(() => { roomIdRef.current = roomId; setShared(new Set()); setDiscussing(null); }, [roomId]);

  const sharer = useMemo(
    () => createStorySharer({ postFn: (payload) => TurfTalk.postRoomMessage(roomId, payload), buildBody: buildDiscussMessage }),
    [roomId]
  );

  const hasNews = !embedded && !!(gameId || league || team);

  const onDiscuss = useCallback(async (item) => {
    if (sharer.isInflight(item.id) || sharer.isShared(item.id)) return;
    const myRoom = roomId;
    setDiscussing(item.id);
    const res = await sharer.share(item);
    if (!mountedRef.current || roomIdRef.current !== myRoom) return; // stale completion → ignore
    setDiscussing((d) => (d === item.id ? null : d));
    if (res.status === SHARE_RESULT.SHARED) {
      setShared((s) => (s.has(item.id) ? s : new Set(s).add(item.id)));
      if (!res.deduped) { setInternalRefresh((n) => n + 1); toast.success("Shared to the room"); }
    } else if (res.status === SHARE_RESULT.FAILED) {
      toast.error("Couldn't share to the room. Tap to retry.");
    }
  }, [sharer, roomId]);

  const chat = (
    <Chat
      title={title}
      height={height}
      contextType="room"
      contextRefId={roomId}
      roomId={roomId}
      refreshSignal={internalRefresh + refreshNonce}
      loadFn={(p) => TurfTalk.roomMessages(roomId, p)}
      sendFn={(b) => TurfTalk.postRoomMessage(roomId, b)}
    />
  );

  if (!hasNews) return chat;

  return (
    <div className="grid lg:grid-cols-[1fr_330px] gap-4 items-start" data-testid="room-with-news">
      {chat}
      <GameNews gameId={gameId} league={league} team={team} onDiscuss={onDiscuss} discussing={discussing} shared={shared} height={height} />
    </div>
  );
}
